import React from 'react';
import {
  Ban, CalendarClock, FileWarning, ListChecks, Repeat, Shield, ShieldAlert, UserX, Zap,
} from 'lucide-react';
import { ScoreChip } from '../cliente/ClienteFranjaRiesgo';

const TONOS = {
  red: 'border-red-200 bg-red-50 text-red-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  sky: 'border-sky-200 bg-sky-50 text-sky-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  off: 'border-slate-200 bg-white text-slate-400',
};

const Chip = ({ icono: Icono, texto, tono = 'off', testid, title }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${TONOS[tono]}`}
    data-testid={testid} title={title}>
    <Icono size={13} /> {texto}
  </span>
);

const verificacionWaya = (v) => {
  if (!v?.resultado) return null;
  if (v.es_bloqueada_waya) return { texto: 'Bloqueada en Waya', tono: 'amber' };
  return {
    CON_CVU: { texto: 'Debía baja y sigue activa', tono: 'red' },
    SIN_CVU: { texto: 'Waya: sin CVU', tono: 'emerald' },
    ACTIVA_INFO: { texto: 'Waya: activa', tono: 'sky' },
  }[v.resultado] || { texto: 'Waya: error al verificar', tono: 'off' };
};

// E1/E2: una sola franja de riesgo del caso (reemplaza banners de reactivación, escalamiento y reincidencia).
export const CasoFranjaRiesgo = ({ caso, franja }) => {
  if (!franja) return <div className="mb-4 h-20 animate-pulse rounded-xl bg-slate-100" data-testid="caso-franja-cargando" />;
  const verif = verificacionWaya(caso.waya_verificacion);
  const eliminado = franja.eliminado_waya || caso.cliente_estado_usuario_waya === 'Eliminado';
  const dias = franja.dias_operacion_mas_vieja;
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="caso-franja-riesgo">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Score del cliente</p>
          <ScoreChip score={franja.score} testid="caso-score" />
        </div>
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {verif && (
            <Chip icono={Shield} texto={verif.texto} tono={verif.tono} testid="waya-verif-badge"
              title={`${caso.waya_verificacion.motivo || ''}${caso.waya_verificacion.fecha ? ` · ${new Date(caso.waya_verificacion.fecha).toLocaleString('es-AR')}` : ''}`} />
          )}
          {eliminado ? <Chip icono={UserX} texto="Eliminado en Waya" tono="red" testid="case-eliminado-waya-badge" title="Según el Reporte de usuarios de Waya" />
            : <Chip icono={UserX} texto={franja.estado_usuario_waya ? `Waya: ${franja.estado_usuario_waya}` : 'Waya: sin dato de usuario'} testid="caso-estado-usuario-waya" />}
          <Chip icono={CalendarClock} tono={dias > 90 ? 'amber' : 'off'} testid="caso-dias-operacion"
            texto={dias === null || dias === undefined ? 'Sin fecha de operación' : `${dias} días desde la operación más vieja`} />
          <Chip icono={FileWarning} texto={franja.ros_en_curso ? 'ROS en curso' : 'Sin ROS en curso'} tono={franja.ros_en_curso ? 'red' : 'off'} testid="caso-ros-en-curso" />
          <Chip icono={ShieldAlert} texto={franja.pep ? 'PEP' : 'No PEP'} tono={franja.pep ? 'red' : 'off'} testid="caso-pep" />
          <Chip icono={Ban} texto={franja.cuit_vigilado ? 'CUIT vigilado' : 'CUIT no vigilado'} tono={franja.cuit_vigilado ? 'red' : 'off'} testid="caso-cuit-vigilado" />
          <Chip icono={ListChecks} texto={franja.lista_blanca ? 'En lista blanca' : 'Sin lista blanca'} tono={franja.lista_blanca ? 'sky' : 'off'} testid="caso-lista-blanca" />
        </div>
      </div>
      {(franja.reincidencia || franja.reactivado_desde_vigilancia) && (
        <div className="mt-3 flex flex-col gap-1 border-t border-slate-100 pt-3 text-sm">
          {franja.reincidencia && (
            <p className="flex items-center gap-2 font-medium text-violet-800" data-testid="caso-reincidencia">
              <Repeat size={14} /> Reincidente: {franja.reincidencia.episodios} episodios de {franja.reincidencia.monitor} en 6 meses
            </p>
          )}
          {franja.reactivado_desde_vigilancia && (
            <p className="flex items-center gap-2 font-medium text-amber-800" data-testid="caso-reactivado">
              <Zap size={14} /> Reactivado desde vigilancia
              {franja.fecha_reactivacion && <span className="font-normal text-amber-700">el {new Date(franja.fecha_reactivacion).toLocaleString('es-AR')}</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CasoFranjaRiesgo;
